import type { Patient } from '@medplum/fhirtypes';

import { Grid, Heading, Stack, Text } from '@eluve/components';

import { Field } from '../../components/Field';
import type { FhirView } from '../../core/view-contract';
import { formatHumanName } from '../accessors';
import { formatFhirDate } from '../observation/format';

import { PatientEditView } from './PatientEditView';

/**
 * The exported Patient component. The read card is a demographics header —
 * the anchor of a patient profile; the row variant is a one-line identity
 * summary for lists and reference slots.
 */
export const PatientView: FhirView<Patient> = (props) => {
  if (props.ctx.inList || props.ctx.parent) {
    return <PatientRowView {...props} />;
  }
  if (props.ctx.mode === 'edit') return <PatientEditView {...props} />;
  return <PatientHeaderView {...props} />;
};

function patientName(data: Patient): string {
  const official = data.name?.find((name) => name.use === 'official');
  return formatHumanName(official ?? data.name?.[0]) || 'Patient';
}

function ageText(data: Patient): string | undefined {
  if (!data.birthDate) return undefined;
  const birth = new Date(data.birthDate);
  if (Number.isNaN(birth.getTime())) return undefined;
  const now = new Date();
  let age = now.getUTCFullYear() - birth.getUTCFullYear();
  const monthDelta = now.getUTCMonth() - birth.getUTCMonth();
  if (
    monthDelta < 0 ||
    (monthDelta === 0 && now.getUTCDate() < birth.getUTCDate())
  ) {
    age -= 1;
  }
  return age >= 0 ? `${age}y` : undefined;
}

function genderText(data: Patient): string | undefined {
  if (!data.gender) return undefined;
  return data.gender.charAt(0).toUpperCase() + data.gender.slice(1);
}

/**
 * Index of the identifier typed as a Medical Record Number (v2-0203 `MR`).
 * A cell labeled "MRN" must never fall back to identifier[0] — integration
 * bookkeeping identifiers (internal ids) land there and would masquerade as
 * the chart number. No MR-typed identifier → an index past the array, so
 * Field renders its honest not-recorded state.
 */
function mrnIdentifierIndex(data: Patient): number {
  const identifiers = data.identifier ?? [];
  const index = identifiers.findIndex((identifier) =>
    identifier.type?.coding?.some((coding) => coding.code === 'MR'),
  );
  return index >= 0 ? index : identifiers.length;
}

function addressText(data: Patient): string | undefined {
  const address = data.address?.[0];
  if (!address) return undefined;
  return (
    address.text ??
    [address.line?.join(' '), address.city, address.state, address.postalCode]
      .filter(Boolean)
      .join(', ')
  );
}

/** Demographics header card. */
const PatientHeaderView: FhirView<Patient> = ({ data, ctx }) => {
  const identity = [genderText(data), ageText(data)]
    .filter(Boolean)
    .join(' · ');
  return (
    <div
      data-view="patient-header"
      className="rounded-lg border border-border-primary bg-bg-primary p-4"
    >
      <Stack
        direction="horizontal"
        wrap
        align="baseline"
        className="gap-x-3 gap-y-1"
      >
        <Heading size="lg" level={3} weight="semibold">
          {patientName(data)}
        </Heading>
        {identity && (
          <Text inline size="sm" color="tertiary">
            {identity}
          </Text>
        )}
      </Stack>
      {/* Field: typed path drives extraction + policy identity + DAR + display —
          absent values render their honest state, never a bare em-dash. */}
      <Grid columns={2} className="mt-3 gap-x-6 gap-y-3 sm:grid-cols-4">
        <Field
          label="Date of birth"
          data={data}
          path={['birthDate']}
          render={(value) => formatFhirDate(value)}
        />
        <Field
          label="MRN"
          data={data}
          path={['identifier', mrnIdentifierIndex(data), 'value']}
        />
        <Field
          label="Phone"
          data={data}
          path={['telecom', { system: 'phone' }, 'value']}
        />
        <Field
          label="Email"
          data={data}
          path={['telecom', { system: 'email' }, 'value']}
        />
      </Grid>
      {addressText(data) && (
        <Text size="xs" color="tertiary" className="mt-3">
          {addressText(data)}
        </Text>
      )}
      {ctx.debug && (
        <pre className="text-xs">{JSON.stringify(data, null, 2)}</pre>
      )}
    </div>
  );
};

/** One-line identity row for lists and reference slots. */
const PatientRowView: FhirView<Patient> = ({ data }) => {
  const dob = formatFhirDate(data.birthDate);
  return (
    <Stack
      direction="horizontal"
      gap={2}
      align="center"
      data-view="patient-row"
      className="py-2"
    >
      <Text inline size="sm" weight="regular" className="truncate">
        {patientName(data)}
      </Text>
      {dob && (
        <Text inline size="xs" color="tertiary">
          DOB {dob}
        </Text>
      )}
    </Stack>
  );
};
